// Tooltip used to surface keyboard shortcuts on the elements that
// trigger them. Goal: members learn the shortcut by muscle memory —
// they hover the Send button, see "Send reply ⌘↵", and next time
// their hand finds the keys instead of the mouse.
//
// Why Radix Tooltip (replacing the original CSS-only implementation):
// the CSS version used `position: absolute` inside a wrapping span,
// which works fine in isolation but gets CLIPPED by any ancestor
// with `overflow: hidden`. The action buttons on the ticket page
// sit inside cards / sticky bars / scrollable rails — exactly the
// kind of overflow context that ate the tooltip alive. Radix
// portals the floating element to `document.body` so it can never
// be clipped by an ancestor, regardless of nesting depth.
//
// Same API as before: pass `label` + optional `keys` and `side`.
// Consumers don't change.
//
// Wired Provider: see App.tsx — a single `<TooltipProvider>` lives
// at the AuthedLayout level so the open/close + delay state is
// shared across every shortcut tooltip on screen (matches Radix's
// recommended composition).

import type { ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

interface Props {
  /** The element being annotated. */
  children: ReactNode;
  /** One-line action label, e.g. "Send reply". */
  label: string;
  /** Optional list of keys rendered as <kbd> chips after the label.
   *  Use the same glyphs as the cheatsheet (⌘, ↵, ⇧). */
  keys?: string[];
  /** Where to place the tooltip relative to the trigger. Defaults
   *  to `top` since most consumers sit on a toolbar near the page
   *  bottom. Radix handles collision detection automatically — if
   *  there's no room on `top`, it flips to `bottom`. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional className for the wrapper. */
  className?: string;
}

export function ShortcutTooltip({
  children,
  label,
  keys,
  side = 'top',
  className,
}: Props) {
  return (
    <TooltipPrimitive.Root>
      {/* asChild lets the consumer's element BE the trigger so we
          don't add an extra wrapper that breaks flex/grid layouts. */}
      <TooltipPrimitive.Trigger asChild>
        <span className={cn('inline-flex', className)}>{children}</span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          // collisionPadding keeps the tooltip a few px away from the
          // viewport edges instead of flush; reads cleaner on tooltips
          // that flip-side due to screen edge collisions.
          collisionPadding={8}
          className={cn(
            'z-50 inline-flex items-center gap-1.5 rounded-md bg-foreground px-2 py-1 text-2xs font-medium text-background shadow-lg',
            // Subtle in/out animations matching the rest of the app
            // (Tailwind's tw-animate-css plugin is already used for
            // toasts + dialogs).
            'animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1',
          )}
        >
          <span>{label}</span>
          {keys && keys.length > 0 ? (
            <span className="flex items-center gap-0.5">
              {keys.map((k, i) => (
                <kbd
                  key={i}
                  className="inline-flex h-4 min-w-4 items-center justify-center rounded border border-background/30 bg-background/15 px-1 font-mono text-3xs font-semibold text-background"
                >
                  {k}
                </kbd>
              ))}
            </span>
          ) : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
